import React from 'react'
import {
  List,
  ListItem,
  Button,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import StockCardBatchItem from 'components/stockCardItems/StockCardBatchItem'
import { IStockCard, IStockCardBatch } from 'interfaces/IStockCard'
import LoadBatchAutocomplete from 'shared/autocomplete/loadBatchAutocomplete'
import { IBatch } from 'interfaces/IPremix'

interface Props {
  stockCardData: IStockCard
  inOutDirection: string
  getMatchingBatch: (stockCardBatch: IStockCardBatch) => any
  onChange: (
    value: any,
    key: string,
    incomingStockCardBatch: IStockCardBatch
  ) => void
  onConfirm: () => void
  isLoading: boolean
  isBatchLoading?: boolean
  getValidationMessage: (stockCard: IStockCard) => string | null
  onAddBatch: () => void
  onRemoveBatch: (index: number) => void
  allowAddBatch?: boolean
  isSuperuser?: boolean
  onUpdate?: () => void
  isUpdating?: boolean
  onDelete?: () => void
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
  onPrefill?: () => void
}

export default function BatchDialog(props: Props) {
  const {
    stockCardData,
    inOutDirection,
    getMatchingBatch,
    onChange,
    onConfirm,
    isLoading,
    isBatchLoading,
    getValidationMessage,
    onAddBatch,
    onRemoveBatch,
    isSuperuser,
    onUpdate,
    isUpdating,
    onDelete,
    onPrefill,
    isEditing,
    setIsEditing,
  } = props


  const canEdit = isSuperuser
  const validationMessage = getValidationMessage(stockCardData)

  return (
    <List>
      {canEdit && (
        <ListItem>
          <Box width="100%" display="flex" justifyContent="flex-end" gap={8}>
            {onPrefill && (isEditing || !stockCardData.isConfirmed) && (
              <Button
                variant="contained"
                color="secondary"
                onClick={onPrefill}
                disabled={isLoading}
              >
                Isi Semua Sesuai Jumlah Diminta
              </Button>
            )}
            <Button
              variant="outlined"
              color={isEditing ? 'secondary' : 'primary'}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel Edit' : 'Edit'}
            </Button>
          </Box>
        </ListItem>
      )}
      {stockCardData.stockCardBatches.map((stockCardBatch, index) => (
        <ListItem key={index} divider={index !== stockCardData.stockCardBatches.length - 1}>
          {!stockCardBatch.batchNumber ? (
            <LoadBatchAutocomplete
              premixId={stockCardData.premixId}
              onChange={(e: IBatch) => {
                onChange(e.batchNumber, 'batchNumber', stockCardBatch)
              }}
            />
          ) : (
            <StockCardBatchItem
              stockCardBatch={stockCardBatch}
              inOutDirection={inOutDirection}
              matchingBatch={getMatchingBatch(stockCardBatch)}
              outgoingDestination={stockCardData.outgoingDestination}
              onChange={onChange}
              disabled={!!stockCardData.isConfirmed && !isEditing}
              isBatchLoading={isBatchLoading}
              isSuperuser={isSuperuser}
              isConfirmed={!!stockCardData.isConfirmed}
              premixId={stockCardData.premixId}
              isEditing={isEditing}
              onRemove={isEditing ? () => onRemoveBatch(index) : undefined}
            />
          )}
        </ListItem>
      ))}
      {isEditing && (
        <ListItem>
          <Button variant="outlined" color="primary" onClick={onAddBatch}>
            Tambah Batch
          </Button>
        </ListItem>
      )}
      <ListItem>
        <Box display="flex" gap={8}>
          {isEditing ? (
            <>
              {onUpdate && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onUpdate}
                  disabled={isUpdating}
                  startIcon={
                    isUpdating ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  Update Stock Card
                </Button>
              )}
              {canEdit && onDelete && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onDelete}
                  disabled={isLoading || !!stockCardData.isConfirmed}
                >
                  Delete
                </Button>
              )}
            </>
          ) : (
            <>
              {(!stockCardData.isConfirmed ||
                stockCardData.stockCardBatches?.some((b) => b.confirmedQuantity == null)) && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onConfirm}
                  disabled={isLoading || !!validationMessage}
                  startIcon={
                    isLoading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  Save
                </Button>
              )}
            </>
          )}
        </Box>
      </ListItem>
      {isEditing && !!stockCardData.isConfirmed && (
        <ListItem>
          <Typography color="secondary" variant="body2">
            Kartu stok tidak dapat dihapus karena sudah dikonfirmasi.
          </Typography>
        </ListItem>
      )}
      {validationMessage && !stockCardData.isConfirmed && (
        <ListItem>
          <Alert severity="warning">{validationMessage}</Alert>
        </ListItem>
      )}
    </List>
  );
}
